import {
  BUTTON_COLOR_IDS,
  BUTTON_STYLE_IDS,
  TEXT_COLOR_IDS,
  VERTICAL_ALIGNMENT_IDS,
} from './_types';

import type { AppAlertFormValues, AppAlertType } from './_types';
import type { AppAlert } from 'src/models/app-alert/_types';

type AppAlertPayload = Omit<
  AppAlert,
  'id' | 'onlyShowForOs' | 'createdAt' | 'updatedAt'
>;

export const defaultValues = (
  alertType: AppAlertType
): AppAlertFormValues => ({
  title: '',
  heroImageUrl: '',
  bgImageUrl: '',
  textColor: 'default',
  buttonCopy: '',
  buttonUrl: '',
  headingText: '',
  textBlock: '',
  bgType: 'color',
  bgColor: '',
  bgTransparent: false,
  verticalAlignment: 'flex-start',
  buttonColor: 'primary',
  buttonStyle: 'filled',
  alertType,
  highContrastButton: false,
  hideCloseButton: false,
});

export const readFileAsDataUrl = async (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('Unable to read image file.'));
    reader.readAsDataURL(file);
  });
};

export const normalizeAlertType = (
  value: string | null,
  bannersEnabled: boolean
): AppAlertType => {
  return value === 'banner' && bannersEnabled ? 'banner' : 'modal';
};

const stringOrEmpty = (value: string | null | undefined) => value ?? '';

const booleanOrFalse = (value: boolean | null | undefined) => value ?? false;

const oneOfOrFallback = <T extends string>(
  value: string | null | undefined,
  allowedValues: readonly T[],
  fallback: T
): T => {
  if (value && allowedValues.includes(value as T)) {
    return value as T;
  }

  return fallback;
};

export const buildInitialValues = (
  alert: AppAlert | undefined,
  fallbackAlertType: AppAlertType
): AppAlertFormValues => {
  const initialValues = defaultValues(fallbackAlertType);

  if (!alert) {
    return initialValues;
  }

  return {
    ...initialValues,
    title: stringOrEmpty(alert.title),
    heroImageUrl: stringOrEmpty(alert.heroImageUrl),
    bgImageUrl: stringOrEmpty(alert.bgImageUrl),
    textColor: oneOfOrFallback(
      alert.textColor,
      TEXT_COLOR_IDS,
      initialValues.textColor
    ),
    buttonCopy: stringOrEmpty(alert.buttonCopy),
    buttonUrl: stringOrEmpty(alert.buttonUrl),
    headingText: stringOrEmpty(alert.headingText),
    textBlock: stringOrEmpty(alert.textBlock),
    bgType:
      alert.alertType === 'modal' && alert.bgType === 'image'
        ? 'image'
        : 'color',
    bgColor: stringOrEmpty(alert.bgColor),
    bgTransparent: booleanOrFalse(alert.bgTransparent),
    verticalAlignment: oneOfOrFallback(
      alert.verticalAlignment,
      VERTICAL_ALIGNMENT_IDS,
      initialValues.verticalAlignment
    ),
    buttonColor: oneOfOrFallback(
      alert.buttonColor,
      BUTTON_COLOR_IDS,
      initialValues.buttonColor
    ),
    buttonStyle: oneOfOrFallback(
      alert.buttonStyle,
      BUTTON_STYLE_IDS,
      initialValues.buttonStyle
    ),
    alertType: alert.alertType === 'banner' ? 'banner' : 'modal',
    highContrastButton: booleanOrFalse(alert.highContrastButton),
    hideCloseButton: booleanOrFalse(alert.hideCloseButton),
  };
};

const emptyToNull = (value: string) => {
  const normalizedValue = value.trim();
  return normalizedValue ? normalizedValue : null;
};

export const buildSavePayload = (
  values: AppAlertFormValues
): AppAlertPayload => {
  const basePayload = {
    title: values.title.trim(),
    heroImageUrl: emptyToNull(values.heroImageUrl),
    buttonUrl: emptyToNull(values.buttonUrl),
  };

  if (values.alertType === 'banner') {
    return {
      ...basePayload,
      bgImageUrl: emptyToNull(values.bgImageUrl),
      alertType: 'banner',
      textColor: null,
      buttonCopy: null,
      headingText: null,
      textBlock: null,
      bgType: null,
      bgColor: null,
      bgTransparent: false,
      verticalAlignment: null,
      buttonColor: null,
      buttonStyle: null,
      highContrastButton: false,
      hideCloseButton: false,
    };
  }

  const usesBackgroundImage = values.bgType === 'image';

  return {
    ...basePayload,
    bgImageUrl: usesBackgroundImage ? emptyToNull(values.bgImageUrl) : null,
    textColor: values.textColor || 'default',
    buttonCopy: emptyToNull(values.buttonCopy),
    headingText: emptyToNull(values.headingText),
    textBlock: emptyToNull(values.textBlock),
    bgType: values.bgType,
    bgColor: values.bgType === 'color' ? emptyToNull(values.bgColor) : null,
    bgTransparent: values.bgTransparent,
    verticalAlignment: values.verticalAlignment,
    buttonColor: values.buttonColor,
    buttonStyle: values.buttonStyle,
    alertType: 'modal',
    highContrastButton: usesBackgroundImage ? values.highContrastButton : false,
    hideCloseButton: values.hideCloseButton,
  };
};

export const buildPreviewAlert = (
  values: AppAlertFormValues
): AppAlert => {
  return {
    id: 0,
    ...buildSavePayload(values),
    onlyShowForOs: null,
    createdAt: 0,
    updatedAt: 0,
  };
};
